/**
 * EnrollmentAdmin — Admin-only browser for enrollment records + uploaded documents + payment history.
 *
 * Reads from the dual-collection endpoints so BOTH namespaces surface here:
 *   - enrollments + portal_enrollments
 *   - enrollment_documents + portal_enrollment_documents
 *   - payment_transactions + portal_payment_transactions
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, Download, FileText, DollarSign, Users, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const token = () => localStorage.getItem('academy_token') || '';
const backend = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'enrollments', label: 'Enrollments', icon: Users },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'payments', label: 'Payments', icon: DollarSign },
];

const fmtDate = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch (_) { return String(v).slice(0, 19); }
};
const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};
const fmtAmount = (cents, currency = 'USD') => {
  if (cents == null) return '';
  const v = Number(cents) / 100;
  return v.toLocaleString('en-US', { style: 'currency', currency: (currency || 'USD').toUpperCase() });
};

const EnrollmentAdmin = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('enrollments');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(null); // enrollment detail modal

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [e, d, p] = await Promise.all([
        api.get('/academy/admin/enrollments/all', { headers: authHeaders() }),
        api.get('/academy/admin/enrollment-documents/all', { headers: authHeaders() }),
        api.get('/academy/admin/payment-transactions/all', { headers: authHeaders() }),
      ]);
      setEnrollments(e.data?.enrollments || []);
      setDocuments(d.data?.documents || []);
      setPayments(p.data?.transactions || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Admin access required');
        navigate('/academy/login');
      } else {
        toast.error(err?.response?.data?.detail || err.message || 'Failed to load records');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const src = tab === 'enrollments' ? enrollments : tab === 'documents' ? documents : payments;
    if (!needle) return src;
    return src.filter((r) => JSON.stringify(r).toLowerCase().includes(needle));
  }, [q, tab, enrollments, documents, payments]);

  const downloadUrl = (fileId) => `${backend}/api/academy/admin/enrollment-document/${fileId}/download?auth=${encodeURIComponent(token())}`;

  const counts = {
    enrollments: enrollments.length,
    documents: documents.length,
    payments: payments.length,
  };

  // Health-of-pipeline summary strip
  const summary = useMemo(() => {
    const paidTotal = payments
      .filter((p) => (p.payment_status || p.status || '').toLowerCase() === 'paid')
      .reduce((sum, p) => sum + Number(p.amount_total ?? p.amount ?? 0), 0);
    const isReal = (r) => !((r.email || '') + JSON.stringify(r.data || {})).toLowerCase().includes('@prsvr.school')
      && !((r.email || '')).toLowerCase().includes('example.com');
    const real = enrollments.filter(isReal);
    const paying = real.filter((r) => r.payment_status === 'paid').length;
    const inProgress = real.filter((r) => r.payment_status !== 'paid' && (r.current_step ?? 0) > 0).length;
    const notPaid = real.filter((r) => r.payment_status !== 'paid').length;
    return {
      realFamilies: real.length,
      paying,
      inProgress,
      notPaid,
      collectedUsd: paidTotal / 100,
    };
  }, [enrollments, payments]);

  return (
    <div className="min-h-screen bg-black text-zinc-200 p-4 md:p-8" data-testid="enrollment-admin">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <button
              onClick={() => navigate('/teacher-dashboard')}
              className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-[0.28em] uppercase text-zinc-500 hover:text-amber-400 mb-2"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-3 h-3" /> Teacher Dashboard
            </button>
            <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight" style={{ color: '#F5ECC8', letterSpacing: '-0.02em' }}>
              Enrollment Records
            </h1>
            <div className="text-[10px] font-black tracking-[0.32em] uppercase mt-1" style={{ color: '#C9A961' }}>
              Legacy + Current · Cross-Collection Union
            </div>
          </div>
          <button
            onClick={loadAll}
            className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest"
            data-testid="refresh-records"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : '↻'} Refresh
          </button>
        </div>

        {/* Health-of-pipeline summary */}
        {!loading && summary.realFamilies > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="summary-strip">
            <SummaryTile label="Real Families" value={summary.realFamilies} color="text-white" />
            <SummaryTile label="Paying" value={summary.paying} color="text-emerald-400" />
            <SummaryTile label="In Progress" value={summary.inProgress} color="text-amber-400" />
            <SummaryTile label="Awaiting Payment" value={summary.notPaid} color="text-orange-400" />
            <SummaryTile
              label="Collected"
              value={summary.collectedUsd.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
              color="text-emerald-400"
              testid="summary-collected"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b-2 border-zinc-800 flex items-center gap-1 mb-4 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-3 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${active ? 'border-amber-500 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
                data-testid={`tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label} <span className="text-zinc-600">({counts[t.key]})</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, student, jersey number, sizes…"
            className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500 outline-none text-sm text-zinc-200 pl-10 pr-4 py-2.5"
            data-testid="search-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-3 py-16 justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-zinc-800 py-16 text-center text-zinc-500 text-sm" data-testid="empty-state">
            {q ? `No records match "${q}"` : `No ${tab} yet.`}
          </div>
        ) : tab === 'enrollments' ? (
          <EnrollmentsTable rows={filtered} onOpen={setSelected} />
        ) : tab === 'documents' ? (
          <DocumentsTable rows={filtered} downloadUrl={downloadUrl} />
        ) : (
          <PaymentsTable rows={filtered} />
        )}
      </div>

      {selected && <EnrollmentDetail row={selected} onClose={() => setSelected(null)} downloadUrl={downloadUrl} documents={documents} onMarkedPaid={loadAll} />}
    </div>
  );
};

const SummaryTile = ({ label, value, color, testid }) => (
  <div className="border border-zinc-800 bg-zinc-950 px-4 py-3" data-testid={testid || `summary-${label.toLowerCase().replaceAll(' ','-')}`}>
    <div className="text-[9px] font-black tracking-[0.28em] uppercase text-zinc-500">{label}</div>
    <div className={`text-2xl font-black mt-1 ${color}`}>{value}</div>
  </div>
);

const EnrollmentsTable = ({ rows, onOpen }) => (
  <div className="overflow-x-auto border border-zinc-800">
    <table className="w-full text-xs" data-testid="enrollments-table">
      <thead className="bg-zinc-900 text-zinc-500 uppercase tracking-widest text-[9px]">
        <tr>
          <th className="text-left px-3 py-2">Student</th>
          <th className="text-left px-3 py-2">Parent Email</th>
          <th className="text-left px-3 py-2">Grade</th>
          <th className="text-left px-3 py-2">Step</th>
          <th className="text-left px-3 py-2">Payment</th>
          <th className="text-left px-3 py-2">Polo</th>
          <th className="text-left px-3 py-2">Hoodie</th>
          <th className="text-left px-3 py-2">Shoe</th>
          <th className="text-left px-3 py-2">#</th>
          <th className="text-left px-3 py-2">Kit Name</th>
          <th className="text-left px-3 py-2">Updated</th>
          <th className="text-left px-3 py-2">Src</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const d = r.data || {};
          // Handle BOTH shapes: portal_enrollments (first/last) and legacy enrollments (single name field)
          const student =
            (`${d.student_first_name || ''} ${d.student_last_name || ''}`.trim()) ||
            (d.student_name || '').trim() ||
            (d.student_preferred || '').trim() ||
            '(no name yet)';
          const parentEmail = r.email || d.parent_email || '';
          const parentName =
            (`${d.parent_first_name || ''} ${d.parent_last_name || ''}`.trim()) ||
            (d.parent_name || '').trim();
          return (
            <tr
              key={r.enrollment_id || r.id || r.email}
              onClick={() => onOpen(r)}
              className="border-t border-zinc-900 hover:bg-zinc-900/60 cursor-pointer"
              data-testid={`enrollment-row-${r.enrollment_id || r.id || r.email}`}
            >
              <td className="px-3 py-2 text-white font-bold">{student}</td>
              <td className="px-3 py-2 text-zinc-400">
                {parentEmail}
                {parentName && <div className="text-[10px] text-zinc-600">{parentName}</div>}
              </td>
              <td className="px-3 py-2">{d.student_grade || ''}</td>
              <td className="px-3 py-2">{r.current_step ?? ''}</td>
              <td className="px-3 py-2">
                <span className={r.payment_status === 'paid' ? 'text-emerald-400' : 'text-zinc-500'}>
                  {r.payment_status || 'n/a'}
                </span>
              </td>
              <td className="px-3 py-2">{d.success_kit_polo_size || ''}</td>
              <td className="px-3 py-2">{d.success_kit_hoodie_size || ''}</td>
              <td className="px-3 py-2">{d.success_kit_shoe_size || ''}</td>
              <td className="px-3 py-2 text-amber-400 font-black">{d.success_kit_number || ''}</td>
              <td className="px-3 py-2">{d.success_kit_name || ''}</td>
              <td className="px-3 py-2 text-zinc-500">{fmtDate(r.updated_at || r.created_at)}</td>
              <td className="px-3 py-2 text-[9px] text-zinc-600 uppercase">{(r._source_collection || '').replace('portal_', 'p.')}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const DocumentsTable = ({ rows, downloadUrl }) => (
  <div className="overflow-x-auto border border-zinc-800">
    <table className="w-full text-xs" data-testid="documents-table">
      <thead className="bg-zinc-900 text-zinc-500 uppercase tracking-widest text-[9px]">
        <tr>
          <th className="text-left px-3 py-2">Type</th>
          <th className="text-left px-3 py-2">Filename</th>
          <th className="text-left px-3 py-2">Email</th>
          <th className="text-left px-3 py-2">Size</th>
          <th className="text-left px-3 py-2">Content-Type</th>
          <th className="text-left px-3 py-2">Uploaded</th>
          <th className="text-left px-3 py-2">Src</th>
          <th className="text-left px-3 py-2"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-zinc-900 hover:bg-zinc-900/60" data-testid={`document-row-${r.id}`}>
            <td className="px-3 py-2 text-white font-bold">{r.doc_label || r.doc_type || ''}</td>
            <td className="px-3 py-2 text-zinc-400">{r.original_filename || ''}</td>
            <td className="px-3 py-2 text-zinc-400">{r.email || ''}</td>
            <td className="px-3 py-2 text-zinc-500">{fmtSize(r.size)}</td>
            <td className="px-3 py-2 text-zinc-500">{r.content_type || ''}</td>
            <td className="px-3 py-2 text-zinc-500">{fmtDate(r.created_at)}</td>
            <td className="px-3 py-2 text-[9px] text-zinc-600 uppercase">{(r._source_collection || '').replace('portal_', 'p.')}</td>
            <td className="px-3 py-2">
              <a
                href={downloadUrl(r.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black px-2.5 py-1 text-[9px] font-black uppercase tracking-widest"
                data-testid={`download-${r.id}`}
              >
                <Download className="w-3 h-3" /> View
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PaymentsTable = ({ rows }) => (
  <div className="overflow-x-auto border border-zinc-800">
    <table className="w-full text-xs" data-testid="payments-table">
      <thead className="bg-zinc-900 text-zinc-500 uppercase tracking-widest text-[9px]">
        <tr>
          <th className="text-left px-3 py-2">Email</th>
          <th className="text-left px-3 py-2">Amount</th>
          <th className="text-left px-3 py-2">Status</th>
          <th className="text-left px-3 py-2">Product</th>
          <th className="text-left px-3 py-2">Stripe Session</th>
          <th className="text-left px-3 py-2">Created</th>
          <th className="text-left px-3 py-2">Src</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id || r.session_id || r.stripe_session_id} className="border-t border-zinc-900 hover:bg-zinc-900/60" data-testid={`payment-row-${r.id || r.session_id}`}>
            <td className="px-3 py-2 text-white">{r.email || r.customer_email || ''}</td>
            <td className="px-3 py-2 font-black text-emerald-400">{fmtAmount(r.amount ?? r.amount_total, r.currency)}</td>
            <td className="px-3 py-2">{r.payment_status || r.status || ''}</td>
            <td className="px-3 py-2 text-zinc-400">{r.product_name || r.package_tier || r.type || ''}</td>
            <td className="px-3 py-2 text-zinc-500 font-mono text-[10px]">{(r.stripe_session_id || r.session_id || '').slice(0, 24)}</td>
            <td className="px-3 py-2 text-zinc-500">{fmtDate(r.created_at)}</td>
            <td className="px-3 py-2 text-[9px] text-zinc-600 uppercase">{(r._source_collection || '').replace('portal_', 'p.')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const EnrollmentDetail = ({ row, onClose, downloadUrl, documents, onMarkedPaid }) => {
  const d = row.data || {};
  const relatedDocs = documents.filter((doc) => doc.enrollment_id === row.enrollment_id || doc.email === row.email);
  const isPaid = (row.payment_status || '').toLowerCase() === 'paid';
  const [markingPaid, setMarkingPaid] = useState(false);
  const [showMarkPaidForm, setShowMarkPaidForm] = useState(false);
  const [markPaidNote, setMarkPaidNote] = useState('');

  const handleMarkPaid = async () => {
    const confirmed = window.confirm(
      `Mark this enrollment as PAID?\n\n` +
      `Student: ${(d.student_first_name || '') + ' ' + (d.student_last_name || '') || row.email}\n` +
      `Email: ${row.email}\n\n` +
      `This will:\n` +
      `  • Flip payment_status → paid\n` +
      `  • Advance current_step past the paywall\n` +
      `  • Let the parent log back in and finish enrollment\n\n` +
      `Only do this if you have CONFIRMED payment in the Stripe Dashboard.`
    );
    if (!confirmed) return;

    setMarkingPaid(true);
    try {
      const res = await api.post(
        '/academy/admin/enrollments/mark-paid',
        {
          enrollment_id: row.enrollment_id || undefined,
          email: row.email || undefined,
          note: markPaidNote || 'Confirmed Stripe payment via Dashboard — webhook unavailable at time of deposit.',
        },
        { headers: authHeaders() }
      );
      toast.success(`Marked paid · step advanced to ${res.data?.current_step}`);
      if (onMarkedPaid) onMarkedPaid();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to mark paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="enrollment-detail-modal">
      <div className="bg-zinc-950 border-2 border-amber-500/60 max-w-4xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color: '#C9A961' }}>Enrollment Detail</div>
            <div className="text-xl font-black text-white mt-1">
              {(d.student_first_name || '') + ' ' + (d.student_last_name || '') || row.email}
            </div>
            <div className="text-[10px] text-zinc-500 font-mono mt-1">{row.enrollment_id}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-lg" data-testid="close-detail">✕</button>
        </div>

        {/* ----- Admin Rescue Panel: Mark Paid & Unlock ----- */}
        {!isPaid && (
          <div className="mb-4 border-2 border-amber-500/50 bg-amber-500/5 p-3" data-testid="mark-paid-panel">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-[10px] font-black tracking-[0.28em] uppercase text-amber-400 mb-1">
                  Stuck at Payment · Step {row.current_step ?? '?'}
                </div>
                <div className="text-xs text-zinc-300 leading-relaxed">
                  This parent has NOT been marked paid. If you&apos;ve confirmed the deposit in Stripe (webhook may have failed),
                  click below to unlock their enrollment so they can log back in and finish steps 13-15.
                </div>
                {showMarkPaidForm && (
                  <textarea
                    className="w-full mt-2 text-xs bg-black border border-zinc-800 p-2 text-white"
                    placeholder="Optional note (e.g. Stripe payment intent ID pi_xxx, confirmed on YYYY-MM-DD)"
                    value={markPaidNote}
                    onChange={(e) => setMarkPaidNote(e.target.value)}
                    rows={2}
                    data-testid="mark-paid-note"
                  />
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {!showMarkPaidForm ? (
                  <button
                    onClick={() => setShowMarkPaidForm(true)}
                    className="text-[10px] font-black tracking-widest uppercase border border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-black px-3 py-2 transition-colors"
                    data-testid="show-mark-paid"
                  >
                    Mark Paid &amp; Unlock →
                  </button>
                ) : (
                  <button
                    onClick={handleMarkPaid}
                    disabled={markingPaid}
                    className="text-[10px] font-black tracking-widest uppercase bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50 px-3 py-2 inline-flex items-center gap-1.5"
                    data-testid="confirm-mark-paid"
                  >
                    {markingPaid ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Confirm
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {isPaid && (
          <div className="mb-4 border border-emerald-500/50 bg-emerald-500/5 p-2.5 text-xs text-emerald-300 flex items-center gap-2" data-testid="paid-badge">
            <CheckCircle2 className="w-4 h-4" /> Payment recorded · enrollment unlocked
            {row.manually_marked_paid && (
              <span className="text-[10px] text-emerald-500/70 ml-1">
                (manually marked by {row.manually_marked_paid_by || 'admin'} on {fmtDate(row.manually_marked_paid_at)})
              </span>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3 text-xs">
          {Object.entries(d).map(([k, v]) => (
            <div key={k} className="border-b border-zinc-900 py-1.5">
              <div className="text-[9px] text-zinc-500 uppercase tracking-widest">{k.replaceAll('_', ' ')}</div>
              <div className="text-white text-sm break-words">
                {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
              </div>
            </div>
          ))}
        </div>

        {relatedDocs.length > 0 && (
          <div className="mt-5 border-t border-zinc-800 pt-4">
            <div className="text-[10px] font-black tracking-[0.28em] uppercase text-amber-400 mb-2">
              Uploaded Documents ({relatedDocs.length})
            </div>
            <div className="space-y-1.5">
              {relatedDocs.map((doc) => (
                <a
                  key={doc.id}
                  href={downloadUrl(doc.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 border border-zinc-800 hover:border-amber-500 bg-zinc-900/50 px-3 py-2 text-xs"
                  data-testid={`detail-download-${doc.id}`}
                >
                  <FileText className="w-4 h-4 text-amber-400" />
                  <div className="flex-1">
                    <div className="text-white font-bold">{doc.doc_label || doc.doc_type}</div>
                    <div className="text-[10px] text-zinc-500">{doc.original_filename} · {fmtSize(doc.size)}</div>
                  </div>
                  <Download className="w-3.5 h-3.5 text-zinc-400" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnrollmentAdmin;
